/**
 * Storage Management
 * Handles session ID generation and localStorage operations
 */

const STORAGE_KEY_PREFIX = 'ragsense_';

function scopedKey(key, scope = 'default') {
  const normalizedScope = String(scope || 'default').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${STORAGE_KEY_PREFIX}${normalizedScope}_${key}`;
}

/**
 * Generate a UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Get or create session ID
 */
export function getOrCreateSessionId(scope = 'default') {
  try {
    let sessionId = localStorage.getItem(scopedKey('session_id', scope));
    if (!sessionId) {
      sessionId = generateUUID();
      localStorage.setItem(scopedKey('session_id', scope), sessionId);
    }
    return sessionId;
  } catch (e) {
    // Fallback if localStorage is not available
    console.warn('localStorage not available, using temporary session ID');
    return generateUUID();
  }
}

/**
 * Get or create stable visitor ID.
 */
export function getOrCreateVisitorId(scope = 'default') {
  try {
    let visitorId = localStorage.getItem(scopedKey('visitor_id', scope));
    if (!visitorId) {
      visitorId = generateUUID();
      localStorage.setItem(scopedKey('visitor_id', scope), visitorId);
    }
    return visitorId;
  } catch (e) {
    console.warn('localStorage not available, using temporary visitor ID');
    return generateUUID();
  }
}

/**
 * Set session ID
 */
export function setSessionId(sessionId, scope = 'default') {
  try {
    localStorage.setItem(scopedKey('session_id', scope), sessionId);
  } catch (e) {
    console.warn('Failed to save session ID');
  }
}

/**
 * Get conversation ID
 */
export function getConversationId(scope = 'default') {
  try {
    return localStorage.getItem(scopedKey('conversation_id', scope));
  } catch (e) {
    return null;
  }
}

/**
 * Set conversation ID
 */
export function setConversationId(conversationId, scope = 'default') {
  try {
    localStorage.setItem(scopedKey('conversation_id', scope), conversationId);
  } catch (e) {
    console.warn('Failed to save conversation ID');
  }
}

/**
 * Clear conversation (start fresh)
 */
export function clearConversation(scope = 'default') {
  try {
    localStorage.removeItem(scopedKey('conversation_id', scope));
  } catch (e) {
    console.warn('Failed to clear conversation');
  }
}

/**
 * Display-only transcript persistence so the visible conversation survives
 * page navigation. The server never trusts client history for prompting;
 * this is purely presentational. Capped to the most recent entries.
 */
const TRANSCRIPT_LIMIT = 40;
const TRANSCRIPT_CONTENT_LIMIT = 20000;

export function getStoredTranscript(scope = 'default') {
  try {
    const raw = localStorage.getItem(scopedKey('transcript', scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
      .slice(-TRANSCRIPT_LIMIT);
  } catch (e) {
    return [];
  }
}

export function appendToStoredTranscript(scope = 'default', message = {}) {
  try {
    const transcript = getStoredTranscript(scope);
    const entry = {
      role: message.role,
      content: String(message.content || '').slice(0, TRANSCRIPT_CONTENT_LIMIT),
      ts: Date.now(),
    };
    if (Array.isArray(message.rich_content) && message.rich_content.length) {
      entry.rich_content = message.rich_content;
    }
    transcript.push(entry);
    localStorage.setItem(scopedKey('transcript', scope), JSON.stringify(transcript.slice(-TRANSCRIPT_LIMIT)));
  } catch (e) {
    // Quota exceeded or storage unavailable — transcript restore is best-effort.
  }
}

export function clearStoredTranscript(scope = 'default') {
  try {
    localStorage.removeItem(scopedKey('transcript', scope));
  } catch (e) {
    // ignore
  }
}

/**
 * Get stored value
 */
export function getStoredValue(key) {
  try {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
  } catch (e) {
    return null;
  }
}

/**
 * Set stored value
 */
export function setStoredValue(key, value) {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, value);
  } catch (e) {
    console.warn(`Failed to store ${key}`);
  }
}
